import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/shared/Logo';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import { Wallet, User } from '../types';
import api from '../services/api';
import './TopUpScreen.css';

interface TopUpFormData {
  amount: string;
  paymentMethod: 'shop2shop' | 'kazang' | 'cash_deposit';
  referenceNumber?: string;
  walletId?: string;
}

export const TopUpScreen: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<TopUpFormData>({
    amount: '',
    paymentMethod: 'cash_deposit'
  });
  const [step, setStep] = useState<'amount' | 'payment' | 'instructions' | 'confirm' | 'success'>('amount');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [profile, setProfile] = useState<User | null>(null);

  useEffect(() => {
    api.users.getProfile().then(setProfile).catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    const loadWallets = async () => {
      try {
        const walletsData = await api.wallets.getWallets();
        setWallets(walletsData);
        // Default to Main wallet if available
        const mainWallet = walletsData.find(w => w.type === 'Main');
        if (mainWallet) {
          setSelectedWallet(mainWallet);
          setFormData(prev => ({ ...prev, walletId: mainWallet.id }));
        }
      } catch (error) {
        console.error('Failed to load wallets:', error);
        setError('Failed to load wallets');
      }
    };

    loadWallets();
  }, []);

  const quickAmounts = [50, 100, 200, 500, 1000];

  const handleAmountSelect = (amount: number) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString()
    }));
  };

  const handleInputChange = (field: keyof TopUpFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const validateAmount = () => {
    const amount = parseFloat(formData.amount);
    if (!formData.amount || amount <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    if (amount < 10) {
      setError('Minimum top-up amount is R10.00');
      return false;
    }
    if (amount > 5000) {
      setError('Maximum top-up amount is R5,000.00');
      return false;
    }
    return true;
  };

  const generateReferenceNumber = () => {
    // Generate unique reference number (in real app, this would come from backend)
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `ABC${timestamp}${random}`;
  };

  const handleContinueFromAmount = () => {
    if (validateAmount()) {
      setStep('payment');
    }
  };

  const handleContinueFromPayment = () => {
    if (formData.paymentMethod === 'cash_deposit') {
      // Generate reference number for cash deposit
      const refNumber = generateReferenceNumber();
      setFormData(prev => ({ ...prev, referenceNumber: refNumber }));
      setStep('instructions');
    } else {
      setStep('confirm');
    }
  };

  const handleContinueFromInstructions = () => {
    setStep('confirm');
  };

  const handleConfirmTopUp = async () => {
    if (!selectedWallet || !formData.walletId) {
      setError('No wallet selected');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const amount = parseFloat(formData.amount);
      const description = `Top-up via ${formData.paymentMethod.replace('_', ' ')}`;
      const reference = formData.referenceNumber || `TOPUP-${Date.now()}`;

      await api.wallets.topUpWallet(
        formData.walletId,
        amount,
        description,
        reference
      );

      setStep('success');
    } catch (err: any) {
      console.error('Top-up failed:', err);
      setError(err.response?.data?.message || 'Top-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `R${amount.toFixed(2)}`;
  };


  if (step === 'success') {
    return (
      <div className="topup-screen">
        <div className="screen-header">
          <div className="header-left">
            <div></div>
            <h1>Success!</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>
        
        <div className="topup-container">
          
          <div className="screen-content">
            <div className="success-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h2>Top-up Request Submitted!</h2>
              <div className="processing-info">
                <p><strong>Amount:</strong> {formatCurrency(parseFloat(formData.amount))}</p>
                <p><strong>Wallet:</strong> {selectedWallet?.type} Wallet</p>
                <p><strong>Reference:</strong> {formData.referenceNumber}</p>
                <div className="processing-message">
                  <i className="fas fa-clock"></i>
                  <p>Your top-up is being processed and will reflect in your account within <strong>1-2 hours</strong>.</p>
                </div>
              </div>
              <div className="success-actions">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="primary-button"
                >
                  Back to Dashboard
                </button>
                <button 
                  onClick={() => {
                    setStep('amount');
                    setFormData({
                      amount: '',
                      paymentMethod: 'cash_deposit'
                    });
                  }}
                  className="secondary-button"
                >
                  Top Up Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'confirm') {
    return (
      <div className="topup-screen">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => setStep('payment')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Confirm Top-up</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>
        
        <div className="topup-container">

          <div className="screen-content">
            <div className="confirmation-details">
            <div className="topup-summary">
              <div className="amount-display">
                <span className="currency">R</span>
                <span className="amount">{parseFloat(formData.amount).toFixed(2)}</span>
              </div>
              
              <div className="topup-info">
                <div className="info-row">
                  <span className="label">Current Balance:</span>
                  <span className="value">{formatCurrency(selectedWallet?.totalBalance || 0)}</span>
                </div>
                <div className="info-row">
                  <span className="label">New Balance:</span>
                  <span className="value">{formatCurrency((selectedWallet?.totalBalance || 0) + parseFloat(formData.amount))}</span>
                </div>
                <div className="info-row">
                  <span className="label">Payment Method:</span>
                  <span className="value">
                    {formData.paymentMethod === 'shop2shop' && 'Shop2Shop'}
                    {formData.paymentMethod === 'kazang' && 'Kazang'}
                    {formData.paymentMethod === 'cash_deposit' && 'Cash Deposit'}
                  </span>
                </div>
                {formData.paymentMethod === 'cash_deposit' && formData.referenceNumber && (
                  <div className="info-row">
                    <span className="label">Reference Number:</span>
                    <span className="value">{formData.referenceNumber}</span>
                  </div>
                )}
              </div>
            </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="confirmation-actions">
              <button 
                onClick={handleConfirmTopUp}
                disabled={loading}
                className="primary-button"
              >
                {loading ? 'Processing...' : 'Confirm Top-up'}
              </button>
              <button 
                onClick={() => setStep('payment')}
                disabled={loading}
                className="secondary-button"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'instructions') {
    return (
      <div className="topup-screen">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => setStep('payment')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Cash Deposit Instructions</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>
        
        <div className="topup-container">

          <div className="screen-content">
            <div className="instructions-content">
            <div className="amount-reminder">
              <p>Amount to Deposit: <strong>{formatCurrency(parseFloat(formData.amount))}</strong></p>
              <p>Reference Number: <strong>{formData.referenceNumber}</strong></p>
            </div>

            <div className="instructions-steps">
              <h3>Follow these steps at any ABSA ATM:</h3>
              <ol className="step-list">
                <li>
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <strong>Visit nearest ABSA ATM</strong>
                    <p>Find any ABSA ATM near you</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <strong>Select Cardless services</strong>
                    <p>Choose this option from the main menu</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <strong>Then select cash deposit</strong>
                    <p>From the cardless services menu</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <strong>Enter account number: 123456789</strong>
                    <p>Use this exact account number</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <strong>Enter reference: {formData.referenceNumber}</strong>
                    <p>This is your unique reference number</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">6</div>
                  <div className="step-content">
                    <strong>Insert cash into the ATM</strong>
                    <p>Insert exactly {formatCurrency(parseFloat(formData.amount))}</p>
                  </div>
                </li>
                <li>
                  <div className="step-number">7</div>
                  <div className="step-content">
                    <strong>Conclude transaction and collect receipt</strong>
                    <p>Keep your receipt as proof of deposit</p>
                  </div>
                </li>
              </ol>
            </div>

            <div className="important-note">
              <i className="fas fa-exclamation-triangle"></i>
              <p><strong>Important:</strong> After completing the deposit, tap "I've Made the Deposit" below. Your transaction will appear as pending in your history until processed.</p>
            </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button 
                onClick={handleContinueFromInstructions}
                className="primary-button"
              >
                I've Made the Deposit
              </button>
              <button 
                onClick={() => setStep('payment')}
                className="secondary-button"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'payment') {
    return (
      <div className="topup-screen">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => setStep('amount')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>Payment Method</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>
        
        <div className="topup-container">

          <div className="screen-content">
            <div className="payment-methods">
            <div className="method-tabs">
              <button 
                className={`method-tab ${formData.paymentMethod === 'cash_deposit' ? 'active' : ''}`}
                onClick={() => handleInputChange('paymentMethod', 'cash_deposit')}
              >
                <i className="fas fa-university"></i>
                Cash Deposit
              </button>
              <button
                className={`method-tab ${formData.paymentMethod === 'shop2shop' ? 'active' : ''}`}
                onClick={() => handleInputChange('paymentMethod', 'shop2shop')}
              >
                <i className="fas fa-store"></i>
                Shop2Shop
              </button>
              <button 
                className={`method-tab ${formData.paymentMethod === 'kazang' ? 'active' : ''} disabled`}
                disabled
                title="Coming Soon"
              >
                <i className="fas fa-mobile-alt"></i>
                Kazang
              </button>
            </div>

            {formData.paymentMethod === 'shop2shop' && (
              <div className="payment-form">
                {!profile?.shop2shopAccount ? (
                  <div className="shop2shop-empty">
                    <i className="fas fa-link"></i>
                    <h3>Link your Shop2Shop account first</h3>
                    <p>You need to link your Shop2Shop account in your profile before you can top up using Shop2Shop.</p>
                    <button
                      className="primary-btn"
                      onClick={() => navigate('/profile', { state: { startEditing: true } })}
                    >
                      <i className="fas fa-user-cog"></i> Link Shop2Shop in Profile
                    </button>
                  </div>
                ) : (
                  <div className="shop2shop-linked">
                    <img
                      src="/shop2shop-merchant-qr.png"
                      alt="Yebo Fresh Shop2Shop QR"
                      className="shop2shop-qr"
                    />
                    <div className="shop2shop-linked-shop">
                      <i className="fas fa-store"></i>
                      <span>Sending from your linked shop <strong>{profile.shop2shopAccount}</strong></span>
                    </div>
                    {profile.shop2shopUserRef && (
                      <div className="shop2shop-ref">
                        <span className="ref-label">Your reference</span>
                        <span className="ref-value">{profile.shop2shopUserRef}</span>
                        <button
                          type="button"
                          className="ref-copy"
                          onClick={() => profile.shop2shopUserRef && navigator.clipboard.writeText(profile.shop2shopUserRef)}
                          aria-label="Copy reference"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      </div>
                    )}
                    <p className="shop2shop-instructions">
                      Open Shop2Shop, scan this QR, and transfer to <strong>Yebo Fresh Johannesburg</strong>.
                      {profile.shop2shopUserRef && (
                        <> If you are paying from a different Shop2Shop account, enter <strong>{profile.shop2shopUserRef}</strong> as the reference so we know who to credit.</>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {formData.paymentMethod === 'kazang' && (
              <div className="payment-form">
                <div className="info-message disabled">
                  <i className="fas fa-info-circle"></i>
                  <p>Kazang payment method is coming soon. Please use Cash Deposit for now.</p>
                </div>
              </div>
            )}

            {formData.paymentMethod === 'cash_deposit' && (
              <div className="payment-form">
                <div className="info-message">
                  <i className="fas fa-info-circle"></i>
                  <p>You will receive step-by-step instructions to deposit cash at any ABSA ATM using our cardless deposit service.</p>
                </div>
              </div>
            )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button 
                onClick={handleContinueFromPayment}
                className="primary-button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="topup-screen">
      <div className="screen-header">
        <div className="header-left">
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <i className="fas fa-arrow-left"></i>
          </button>
          <h1>Top Up Wallet</h1>
        </div>
        <div className="header-right">
          <Logo size="small" variant="light" />
        </div>
      </div>
      
      <div className="topup-container">

        <div className="screen-content">
          <div className="current-balance">
            <p>Current Balance - {selectedWallet?.type} Wallet</p>
            <h2>{formatCurrency(selectedWallet?.totalBalance || 0)}</h2>
          </div>

        <div className="amount-section">
          <label htmlFor="amount">Top-up Amount</label>
          <div className="amount-input-container">
            <span className="currency-symbol">R</span>
            <input
              id="amount"
              type="number"
              step="0.01"
              min="10"
              max="5000"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              className="amount-input"
            />
          </div>
          <div className="amount-limits">
            Min: R10.00 • Max: R5,000.00
          </div>
        </div>

        <div className="quick-amounts">
          <p>Quick Amounts</p>
          <div className="quick-amount-buttons">
            {quickAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => handleAmountSelect(amount)}
                className={`quick-amount-button ${formData.amount === amount.toString() ? 'selected' : ''}`}
              >
                R{amount}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button 
              onClick={handleContinueFromAmount}
              className="primary-button"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};